import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Scissors, SprayCan, Waves, ArrowRight, Check } from 'lucide-react';
import { Reveal, useTilt } from '../motion';

const CARDS = [
  {
    id: 'beauty-clinic',
    to: '/healthcare-providers?category=Beauty%20Clinic',
    icon: Sparkles,
    image:
      'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    titleKey: 'home.categories.beautyClinic',
    titleFallback: 'Beauty Clinics',
    descKey: 'home.categories.beautyClinicDesc',
    descFallback: 'Professional skincare, aesthetics, and beauty treatments',
    items: [
      ['home.categories.beautyClinicItems.skincare', 'Skincare Treatments'],
      ['home.categories.beautyClinicItems.facials', 'Facials & Peels'],
      ['home.categories.beautyClinicItems.laser', 'Laser Treatments'],
      ['home.categories.beautyClinicItems.botox', 'Botox & Fillers'],
    ],
    badge: 'bg-pink-500/90',
    dot: 'text-pink-500',
    ring: 'hover:border-pink-300/80 dark:hover:border-pink-400/40',
    glow: 'from-pink-500/25 to-rose-500/10',
  },
  {
    id: 'salon',
    to: '/healthcare-providers?category=Salon',
    icon: Scissors,
    image:
      'https://images.unsplash.com/photo-1626379501846-0df4067b8bb9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    titleKey: 'home.categories.salon',
    titleFallback: 'Ladies Salons',
    descKey: 'home.categories.salonDesc',
    descFallback: 'Hair, nails and beauty for women',
    items: [
      ['home.categories.salonItems.hair', 'Hair Styling & Cuts'],
      ['home.categories.salonItems.nails', 'Nail Services'],
      ['home.categories.salonItems.makeup', 'Makeup & Bridal'],
      ['home.categories.salonItems.coloring', 'Hair Coloring'],
    ],
    badge: 'bg-emerald-500/90',
    dot: 'text-emerald-500',
    ring: 'hover:border-emerald-300/80 dark:hover:border-emerald-400/40',
    glow: 'from-emerald-500/25 to-green-500/10',
  },
  {
    id: 'barbershop',
    to: '/healthcare-providers?category=Barbershop',
    icon: SprayCan,
    image:
      'https://images.unsplash.com/photo-1536520002442-39764a41e987?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    titleKey: 'home.categories.barbershop',
    titleFallback: 'Barbershops',
    descKey: 'home.categories.barbershopDesc',
    descFallback: "Haircuts, beards and grooming for men",
    items: [
      ['home.categories.barbershopItems.haircut', 'Haircuts & Fades'],
      ['home.categories.barbershopItems.beard', 'Beard Trim & Shaping'],
      ['home.categories.barbershopItems.shave', 'Hot Towel Shave'],
      ['home.categories.barbershopItems.kids', "Kids' Haircuts"],
    ],
    badge: 'bg-amber-600/90',
    dot: 'text-amber-600',
    ring: 'hover:border-amber-300/80 dark:hover:border-amber-400/40',
    glow: 'from-amber-500/25 to-orange-500/10',
  },
  {
    id: 'spa',
    to: '/healthcare-providers?category=Spa',
    icon: Waves,
    image:
      'https://images.unsplash.com/photo-1600334129128-685c5582fd35?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    titleKey: 'home.categories.spa',
    titleFallback: 'Spa',
    descKey: 'home.categories.spaDesc',
    descFallback: 'Relaxation, massage, and wellness treatments',
    items: [
      ['home.categories.spaItems.massage', 'Massage Therapy'],
      ['home.categories.spaItems.body', 'Body Treatments'],
      ['home.categories.spaItems.hammam', 'Hammam & Steam'],
      ['home.categories.spaItems.aromatherapy', 'Aromatherapy'],
    ],
    badge: 'bg-teal-500/90',
    dot: 'text-teal-500',
    ring: 'hover:border-teal-300/80 dark:hover:border-teal-400/40',
    glow: 'from-teal-500/25 to-cyan-500/10',
  },
];

const CategoryCard = ({ card, delay }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tiltRef = useTilt(5);
  const Icon = card.icon;

  return (
    <Reveal delay={delay} className="tilt-stage">
      <button
        ref={tiltRef}
        onClick={() => navigate(card.to)}
        data-testid={`category-card-${card.id}`}
        className={`tilt-card group relative w-full text-start rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 overflow-hidden shadow-sm hover:shadow-2xl ${card.ring}`}
      >
        <span
          className={`pointer-events-none absolute inset-x-0 -top-24 h-48 bg-gradient-to-b ${card.glow} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-2xl`}
        />
        <div className="relative overflow-hidden">
          <img
            src={card.image}
            alt=""
            loading="lazy"
            className="w-full h-48 object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.07]"
          />
          <span className="absolute inset-0 bg-gradient-to-t from-gray-950/60 to-transparent" />
          <span
            className={`absolute bottom-4 start-4 grid place-items-center w-11 h-11 rounded-2xl ${card.badge} backdrop-blur-sm shadow-lg`}
          >
            <Icon className="w-5 h-5 text-white" />
          </span>
        </div>

        <div className="relative p-6 sm:p-7">
          <h3 className="font-display text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t(card.titleKey, card.titleFallback)}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
            {t(card.descKey, card.descFallback)}
          </p>
          <ul className="mt-5 space-y-2.5">
            {card.items.map(([key, fallback]) => (
              <li key={key} className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                <Check className={`w-4 h-4 shrink-0 ${card.dot}`} strokeWidth={3} />
                {t(key, fallback)}
              </li>
            ))}
          </ul>
          <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            {t('landing.explore', 'Explore')}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
          </span>
        </div>
      </button>
    </Reveal>
  );
};

const Categories = () => {
  const { t } = useTranslation();

  return (
    <section
      data-testid="categories-section"
      className="relative bg-white dark:bg-gray-950 py-20 sm:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400">
            {t('landing.categoriesLabel', 'Categories')}
          </p>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
            {t('home.whatCanYouBook', 'What Can You Book?')}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-400">
            {t('home.whatCanYouBookDesc', 'Beauty clinics, ladies salons, barbershops and spa treatments - all in one place')}
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
          {CARDS.map((card, i) => (
            <CategoryCard key={card.id} card={card} delay={i * 110} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Categories;
